import React, { Component } from "react";
import { Modal, Header, Button, Icon } from "semantic-ui-react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import ReactRouterPause from "@allpro/react-router-pause";
import { withTranslation } from "react-i18next";

@observer
class ModalUnsavedConfirmation extends Component {
  constructor(props) {
    super(props);

    this.beforeUnloadHandler = this.beforeUnloadHandler.bind(this);

    this.state = {
      showModal: false,
      navigationResumeWithoutSaving: null,
      navigationSaveAndResume: null,
      navigationCancel: null,
    };
  }

  componentDidMount() {
    window.addEventListener("beforeunload", this.beforeUnloadHandler);
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.beforeUnloadHandler);
  }

  handleNavigationAttempt(navigation) {
    const { onSave, onDiscard } = this.props;

    this.setState({
      showModal: true,
      navigationSaveAndResume: () => {
        // if this function is called then we assume that onSave was set, otherwise it will be an
        // invalid state.
        if (!onSave) {
          throw new Error("onSave() callback is required when calling navigationSaveAndResume()");
        }

        this.setState({ showModal: false });
        // onSaveChange might return a promise.
        const result = onSave();
        // check if result is a Promise
        if (result && typeof result.then !== "undefined") {
          result.then(() => navigation.resume()).catch(() => navigation.cancel());
        } else {
          // If not a promise, then resume immediately.
          navigation.resume();
        }
      },
      navigationResumeWithoutSaving: () => {
        // onDiscard is optional.
        const result = onDiscard ? onDiscard() : null;

        // check if we got a result and if that result is a promise
        if (result && typeof result.then !== "undefined") {
          result.then(() => navigation.resume()).catch(() => navigation.cancel());
        } else {
          navigation.resume();
        }
      },
      navigationCancel: () => {
        this.setState({ showModal: false });
        navigation.cancel();
      },
    });

    // pause navigation by returning null (as specified by its API)
    return null;
  }

  beforeUnloadHandler(event) {
    const { when } = this.props;

    if (when) {
      // Cancel the unload event, this is done by showing a warning popup, will prevents the tab/window  to be closed.
      // If you prevent default behavior in Mozilla Firefox prompt will always be shown.
      event.preventDefault();
      // Chrome requires returnValue to be set
      // eslint-disable-next-line no-param-reassign
      event.returnValue = "";
    } else {
      // the absence of a returnValue property on the event will guarantee the browser unload happens
      // eslint-disable-next-line no-param-reassign
      delete event.returnValue;
    }
  }

  render() {
    const { when, onSave, t } = this.props;
    const { showModal, navigationResumeWithoutSaving, navigationSaveAndResume, navigationCancel } = this.state;

    return (
      <>
        <ReactRouterPause handler={navigation => this.handleNavigationAttempt(navigation)} when={when} />
        <Modal open={showModal}>
          <Header icon="exclamation circle" content={t("messages:unsavedChanges")} />
          <Modal.Content>
            <p>{t("messages:unsavedChangesMessage")}</p>
          </Modal.Content>
          <Modal.Actions>
            {onSave && (
              <Button color="blue" inverted onClick={() => navigationSaveAndResume()}>
                <Icon name="save" /> {t("messages:save")}
              </Button>
            )}
            <Button color="red" inverted onClick={() => navigationResumeWithoutSaving()}>
              <Icon name="trash alternate outline" /> {t("messages:discard")}
            </Button>
            <Button color="green" inverted onClick={() => navigationCancel()}>
              <Icon name="cancel" /> {t("messages:cancel")}
            </Button>
          </Modal.Actions>
        </Modal>
      </>
    );
  }
}
ModalUnsavedConfirmation.propTypes = {
  when: PropTypes.bool.isRequired,
  // Both functions below are optional. If set, they should ideally return a Promise (though not mandatory).
  // This will ensure that e.g we save changes first and only once fulfilled then navigate away.
  onSave: PropTypes.func,
  onDiscard: PropTypes.func,
  t: PropTypes.func.isRequired,
};
export default withTranslation()(ModalUnsavedConfirmation);
